import { Modal, Progress, Tag, Alert, Button, List, Spin } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import type { QCItemStatus, SlideQCItem } from '../../hooks/useSlideQC';

const statusTag = (status: QCItemStatus) => {
  switch (status) {
    case 'pending':
      return <Tag icon={<ClockCircleOutlined />} color="default">대기</Tag>;
    case 'checking':
      return <Tag icon={<LoadingOutlined />} color="processing">검사 중</Tag>;
    case 'valid':
      return <Tag icon={<CheckCircleOutlined />} color="success">정상</Tag>;
    case 'invalid':
      return <Tag icon={<CloseCircleOutlined />} color="error">손상</Tag>;
    case 'error':
      return <Tag icon={<ExclamationCircleOutlined />} color="warning">오류</Tag>;
    case 'skipped':
      return <Tag icon={<MinusCircleOutlined />} color="default">미실시</Tag>;
  }
};

interface Props {
  open: boolean;
  items: SlideQCItem[];
  done: boolean;
  hasIssue: boolean;
  onClose: () => void;
}

export const SlideQCModal: React.FC<Props> = ({ open, items, done, hasIssue, onClose }) => {
  // 서버에서 슬라이드 정보가 아직 하나도 오지 않은 상태
  const isWaiting = open && !done && items.length > 0 && items.every(i => !i.filename);
  const checked = items.filter(i => i.status !== 'pending' && i.status !== 'checking').length;
  const progress = items.length > 0 ? Math.round((checked / items.length) * 100) : 0;

  return (
    <Modal
      open={open}
      centered
      title="슬라이드 QC 검사"
      footer={done ? (
        <Button type={hasIssue ? 'default' : 'primary'} onClick={onClose}>
          확인
        </Button>
      ) : null}
      closable={false}
      maskClosable={false}
      width={520}
      styles={{
        content: { maxHeight: '60vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px 24px' },
      }}
    >
      <div style={{ flexShrink: 0 }}>
        {isWaiting && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, color: '#595959' }}>
            <Spin size="small" />
            <span>서버에서 슬라이드 처리 중 — 완료되는 즉시 검사를 시작합니다.</span>
          </div>
        )}

        {!done && !isWaiting && (
          <p style={{ color: '#595959', marginBottom: 16 }}>
            업로드된 슬라이드 파일의 무결성을 검사합니다.
          </p>
        )}

        {done && !hasIssue && (
          <Alert
            type="success"
            showIcon
            message="모든 슬라이드 파일이 정상입니다."
            style={{ marginBottom: 16 }}
          />
        )}

        {done && hasIssue && (
          <Alert
            type="error"
            showIcon
            message="일부 파일에 문제가 발견되었습니다."
            description="손상 또는 오류가 발생한 파일이 있습니다. 해당 파일을 확인 후 필요 시 재업로드해 주세요."
            style={{ marginBottom: 16 }}
          />
        )}

        <Progress
          percent={progress}
          status={done ? (hasIssue ? 'exception' : 'success') : 'active'}
          style={{ marginBottom: 8 }}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item style={{ padding: '8px 0' }} extra={statusTag(item.status)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.originalFilename}
                </div>
                {item.filename && item.filename !== item.originalFilename && (
                  <div style={{ color: '#8c8c8c', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    서버: {item.filename}
                  </div>
                )}
                {item.status === 'valid' && item.checkedTiles !== undefined && (
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>검사 타일: {item.checkedTiles}</div>
                )}
                {item.status === 'invalid' && item.corruptedCount !== undefined && (
                  <div style={{ color: '#ff4d4f', fontSize: 12 }}>손상 타일: {item.corruptedCount}개</div>
                )}
                {item.status === 'error' && item.errorMsg && (
                  <div style={{ color: '#faad14', fontSize: 12 }}>{item.errorMsg}</div>
                )}
              </div>
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};
